import React from "react";
import { useRowGestures } from "@/lib/useSelection";
import { colorIdToHex } from "@/lib/highlightColors";

/**
 * Single Bible verse row. Selection-aware:
 *   • long-press → starts selection / re-anchors when more than 1.
 *   • short click → toggles (only in selection mode).
 *   • double-click → clears the whole selection.
 * The shared `SelectionActionBar` renders the Copy / Share menu and the
 * five pastel highlight colours for the current selection — this
 * component is purely the row visual.
 *
 * Visual hints:
 *   • Selected: underline + softer background so the user can see what
 *     they're about to act on before opening the highlight bar.
 *   • Highlighted favourite: pastel background painted from the saved
 *     `color_id` so the row reads like a study-book annotation.
 *   • Saved without colour (legacy): subtle left border, unchanged.
 */
export default function VerseRow({
    verse, isSaved, savedColor, isSelected, inSelectionMode,
    onLongPress, onShortClick, onDoubleClick,
}) {
    const handlers = useRowGestures({
        inSelectionMode,
        onLongPress,
        onShortClick,
        onDoubleClick,
    });

    const bg = colorIdToHex(savedColor);

    return (
        <p
            key={verse.verse}
            data-bible-verse={verse.verse}
            data-testid={`bible-verse-${verse.verse}`}
            data-selected={isSelected ? "true" : "false"}
            data-color={savedColor || ""}
            className={[
                "verse-row scroll-mt-36 relative rounded-md transition-colors duration-150",
                "px-3 py-2 -mx-3 cursor-pointer",
                isSaved && !bg ? "border-l-2 border-sangre/50 pl-4" : "",
                isSelected
                    ? "underline decoration-blue-600 decoration-2 underline-offset-4"
                    : "",
            ].filter(Boolean).join(" ")}
            style={{ backgroundColor: bg || undefined }}
            {...handlers}
        >
            <sup className="text-sangre text-xs mr-1 font-semibold">{verse.verse}</sup>
            {verse.text}
            {isSaved && !isSelected && (
                <span className="sr-only" data-testid={`bible-verse-saved-${verse.verse}`}>
                    saved
                </span>
            )}
        </p>
    );
}
